/**
 * Reusable Support page used by Parent / Observer / Principal portals.
 * Renders the ticket list, "New Ticket" modal, and the conversation/reply
 * dialog for any single ticket. All three roles use the same /api/tickets
 * endpoints; only the auth token storage key differs.
 *
 * Props:
 *   tokenKey        — localStorage key (parent_token | observer_token | principal_token)
 *   role            — "parent" | "observer" | "principal" (display only)
 *   accentColor     — Tailwind color name (indigo, sky, etc.)
 *   onBack          — () => void, navigates to that role's dashboard
 *   portalTitle     — "Support Center" or similar
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  HelpCircle, Plus, ArrowLeft, MessageSquare, Clock,
  CheckCircle, AlertCircle, Loader2, Send, X, Ticket as TicketIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupportTicketsPage({
  tokenKey,
  role,
  onBack,
  portalTitle = 'Support Center',
  headerless = false,
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const authH = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` } }),
    [tokenKey],
  );

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const fetchData = async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/tickets`, authH),
        axios.get(`${API_URL}/api/tickets/categories`),
      ]);
      setTickets(tRes.data.tickets || []);
      setStatusCounts(tRes.data.status_counts || {});
      setCategories(cRes.data.categories || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load support data', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const refreshAndPickTicket = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/tickets/${id}`, authH);
      setSelectedTicket(r.data);
      // also refresh list silently
      const lst = await axios.get(`${API_URL}/api/tickets`, authH);
      setTickets(lst.data.tickets || []);
      setStatusCounts(lst.data.status_counts || {});
    } catch (e) { /* ignore */ }
  };

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  if (loading) {
    return (
      <div className={`${headerless ? '' : 'min-h-screen bg-indigo-50/40'} flex items-center justify-center py-20`}>
        <Loader2 className={`w-8 h-8 animate-spin ${headerless ? 'text-orange-500' : 'text-indigo-500'}`} />
      </div>
    );
  }

  return (
    <div className={`${headerless ? '' : 'min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50/30'}`} data-testid="support-page-root">
      {/* Header — hidden when parent/observer nav already provides it */}
      {!headerless && (
        <header className="bg-white/90 backdrop-blur border-b sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <Button variant="ghost" size="icon" onClick={onBack} data-testid="support-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <HelpCircle className={`w-7 h-7 text-indigo-500`} />
                  {portalTitle}
                </h1>
                <p className="text-sm text-gray-500">Raise a ticket — our team responds within 24–48 hours.</p>
              </div>
            </div>
            <Button onClick={() => setShowNew(true)} className={`bg-indigo-500 hover:bg-indigo-600 text-white`} data-testid="support-new-ticket-btn">
              <Plus className="w-4 h-4 mr-2" /> New Ticket
            </Button>
          </div>
        </header>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* New ticket button row — only when header is hidden */}
        {headerless && (
          <div className="flex justify-end">
            <Button
              onClick={() => setShowNew(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="support-new-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Ticket
            </Button>
          </div>
        )}
        {/* Status cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="All" value={tickets.length} icon={TicketIcon} active={filter === 'all'} onClick={() => setFilter('all')} color="slate" />
          <StatCard label="Open" value={statusCounts.open || 0} icon={AlertCircle} active={filter === 'open'} onClick={() => setFilter('open')} color="blue" />
          <StatCard label="In Progress" value={statusCounts.in_progress || 0} icon={Clock} active={filter === 'in_progress'} onClick={() => setFilter('in_progress')} color="amber" />
          <StatCard label="Resolved" value={statusCounts.resolved || 0} icon={CheckCircle} active={filter === 'resolved'} onClick={() => setFilter('resolved')} color="emerald" />
        </div>

        {/* Tickets list */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <TicketIcon className="w-16 h-16 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500">No tickets yet.</p>
                <Button variant="link" onClick={() => setShowNew(true)} data-testid="support-empty-create">
                  Raise your first ticket →
                </Button>
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map((t) => (
                  <li
                    key={t.id}
                    onClick={() => refreshAndPickTicket(t.id)}
                    className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <StatusDot status={t.status} />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{t.subject}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t.ticket_number} • {t.category} • {new Date(t.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {t.responses?.length > 0 && (
                        <span className="inline-flex items-center text-xs text-gray-500 gap-1">
                          <MessageSquare className="w-3.5 h-3.5" /> {t.responses.length}
                        </span>
                      )}
                      <PriorityChip p={t.priority} />
                      <StatusChip s={t.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>

      {showNew && (
        <NewTicketModal
          categories={categories}
          onClose={() => setShowNew(false)}
          onCreated={async (id) => {
            setShowNew(false);
            toast({ title: 'Ticket created' });
            await fetchData();
            if (id) refreshAndPickTicket(id);
          }}
          authH={authH}
        />
      )}

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onReplied={() => refreshAndPickTicket(selectedTicket.id)}
          role={role}
          authH={authH}
        />
      )}
    </div>
  );
}

// ===================== Sub-components =====================

function StatCard({ label, value, icon: Icon, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`text-left border rounded-xl p-4 bg-white transition-all ${active ? `ring-2 ring-${color}-500 shadow-md` : 'hover:shadow-sm'}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
        <Icon className={`w-5 h-5 text-${color}-500`} />
      </div>
      <div className="text-3xl font-bold text-gray-900 mt-2">{value}</div>
    </button>
  );
}

function StatusDot({ status }) {
  const cls =
    status === 'open' ? 'bg-blue-500' :
    status === 'in_progress' ? 'bg-amber-500' :
    status === 'resolved' ? 'bg-emerald-500' :
    'bg-gray-400';
  return <span className={`w-2.5 h-2.5 rounded-full ${cls} mt-1.5 flex-shrink-0`} />;
}

function StatusChip({ s }) {
  const map = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-gray-200 text-gray-700',
  };
  return <span className={`text-xs px-2 py-1 rounded-full capitalize ${map[s] || 'bg-gray-100'}`}>{(s || '').replace('_', ' ')}</span>;
}

function PriorityChip({ p }) {
  const map = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
  };
  return <span className={`text-xs px-2 py-1 rounded-full capitalize ${map[p] || 'bg-gray-100'}`}>{p}</span>;
}

function NewTicketModal({ categories, onClose, onCreated, authH }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ category: '', subject: '', description: '', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.category || !form.subject.trim() || !form.description.trim()) {
      toast({ title: 'Missing info', description: 'Category, subject and description are required.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/tickets`, form, authH);
      onCreated(data.ticket?.id);
    } catch (e) {
      toast({ title: 'Failed to create', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-ticket-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Create Support Ticket</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setForm({ ...form, category: c.id })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    form.category === c.id
                      ? `border-indigo-500 bg-indigo-50`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`category-${c.id}`}
                >
                  <span className="text-lg mr-2">{c.icon}</span>
                  <span className="text-sm font-medium">{c.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description" data-testid="new-ticket-subject" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What happened? Steps to reproduce, screenshots, etc." data-testid="new-ticket-description" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <div className="flex gap-2">
              {['low', 'medium', 'high', 'urgent'].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm({ ...form, priority: p })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                    form.priority === p
                      ? p === 'urgent' ? 'bg-red-500 text-white'
                        : p === 'high' ? 'bg-orange-500 text-white'
                        : p === 'medium' ? 'bg-blue-500 text-white'
                        : 'bg-gray-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`priority-${p}`}
                >{p}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            className={`flex-1 bg-indigo-500 hover:bg-indigo-600 text-white`}
            onClick={submit}
            disabled={submitting}
            data-testid="new-ticket-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Send className="w-4 h-4 mr-2" /> Submit</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TicketDetailModal({ ticket, accentColor, onClose, onReplied, role, authH }) {
  const { toast } = useToast();
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  // Build a unified, chronological thread:
  //  - first message: the original description
  //  - subsequent: every entry in `responses`
  const thread = useMemo(() => {
    const first = {
      id: 'opening',
      user_role: ticket.user_role,
      user_name: ticket.user_name || role,
      message: ticket.description,
      created_at: ticket.created_at,
    };
    return [first, ...(ticket.responses || [])];
  }, [ticket, role]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/tickets/${ticket.id}/reply`, { message: reply.trim() }, authH);
      setReply('');
      onReplied();
    } catch (e) {
      toast({ title: 'Failed to send', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSending(false); }
  };

  const isClosed = ticket.status === 'closed';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="ticket-detail-modal">
        {/* Header */}
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className={`text-xs text-indigo-600 font-medium`}>{ticket.ticket_number} • {ticket.category}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{ticket.subject}</h3>
              <div className="flex items-center gap-2 mt-2">
                <StatusChip s={ticket.status} />
                <PriorityChip p={ticket.priority} />
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
          </div>
        </div>

        {/* Conversation */}
        <div className="flex-1 overflow-y-auto p-5 bg-gray-50 space-y-3" data-testid="ticket-thread">
          {thread.map(m => {
            const isAdmin = m.user_role === 'admin';
            return (
              <div key={m.id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 shadow-sm ${
                  isAdmin
                    ? 'bg-white border border-gray-200 rounded-tl-sm'
                    : `bg-indigo-500 text-white rounded-tr-sm`
                }`}>
                  <p className={`text-xs font-medium mb-1 ${isAdmin ? 'text-gray-500' : `text-indigo-100`}`}>
                    {isAdmin ? 'Sanjaya Support' : m.user_name || 'You'}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                  <p className={`text-[10px] mt-1 ${isAdmin ? 'text-gray-400' : `text-indigo-100/80`}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        <div className="p-4 border-t bg-white">
          {isClosed ? (
            <p className="text-sm text-gray-500 text-center">This ticket is closed. Please raise a new one.</p>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder="Type your reply…"
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') send(); }}
                data-testid="ticket-reply-input"
              />
              <Button onClick={send} disabled={sending || !reply.trim()} className={`bg-indigo-500 hover:bg-indigo-600 text-white`} data-testid="ticket-reply-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
